import { colors } from "@/Utils";
import { Layer } from "@/Utils/hierarchy";

import { ComparisonMode } from "../types/comparisonMode";
import { Rating } from "../types/saturationCurve";

export enum InvestmentRating {
  UNDERINVESTED = "Underinvested",
  BALANCED = "Balanced",
  OVERINVESTED = "Overinvested",
  INSUFFICIENT_DATA = "Insufficient Data",
}

export const RatingToInvestmentRatingMap: Record<Rating, InvestmentRating> = {
  [Rating.Negative]: InvestmentRating.UNDERINVESTED,
  [Rating.Neutral]: InvestmentRating.BALANCED,
  [Rating.Positive]: InvestmentRating.OVERINVESTED,
};

export const InvestmentRatingMap = {
  [InvestmentRating.UNDERINVESTED]: {
    id: "underinvested",
    label: "Underinvested",
    tooltip:
      "These channels are underinvested, meaning they have less spend than the optimal point to generate maximum yield before diminishing returns.",
    description: "This category is showing negative performance.",
    labelColor: "#FBE9C9",
    curveColor: colors.underCircleBorder,
  },
  [InvestmentRating.BALANCED]: {
    id: "balanced",
    label: "Balanced",
    tooltip:
      "These channels are at a balanced investment point, meaning they have the right level of weekly spend to generate maximum yield before diminishing returns.",
    description: "This category is showing neutral performance.",
    labelColor: "#C9FBDB",
    curveColor: colors.balancedCircleBorder,
  },
  [InvestmentRating.OVERINVESTED]: {
    id: "overinvested",
    label: "Overinvested",
    tooltip:
      "These channels are considered overinvested. The number in brackets is the difference between your current weekly spend and the estimated point of maximum efficiency.",
    description: "This category is showing positive performance.",
    labelColor: "#FBD7C9",
    curveColor: "none",
  },
  [InvestmentRating.INSUFFICIENT_DATA]: {
    id: "insufficient-data",
    label: "Insufficient Data",
    tooltip:
      "These Saturation Curves cannot be generated either because there isn’t enough data to generate a curve, or the quality of the data is too low to produce a curve with high confidence.",
    description: "There is not enough data to determine the rating for this category.",
    labelColor: "#E7E7E7",
    curveColor: colors.overCircleBorder,
  },
};

export enum CurveType {
  YIELD = "yield",
  MARGINAL_ROI = "marginalROI",
  REVENUE = "revenue",
}

export const layerMap: Record<ComparisonMode, Layer> = {
  [ComparisonMode.BRAND]: Layer.Brand,
  [ComparisonMode.CATEGORY]: Layer.Category,
  [ComparisonMode.PRODUCT]: Layer.Product,
  [ComparisonMode.CHANNEL]: Layer.Portfolio,
  [ComparisonMode.GEOGRAPHY]: Layer.Portfolio,
};

export const CURVE_INFO_MAP = {
  [CurveType.REVENUE]: {
    id: "revenue",
    label: "Revenue",
    description:
      "Plot how changes to media spend impact expected revenue generated. Use this to see how investment levels correlate with overall financial impact.",
  },
  [CurveType.MARGINAL_ROI]: {
    id: "marginalROI",
    label: "Marginal ROAS",
    description:
      "Plot the expected incremental return generated by the last dollar spent. Use this to see where additional spending becomes less efficient or more impactful.",
  },
  [CurveType.YIELD]: {
    id: "yield",
    label: "Yield",
    description:
      "Plot the expected dollar return per dollar spent. Use this to identify the saturation point where additional spend yields minimal returns.",
  },
};

export enum MarkerType {
  LOWER_THRESHOLD = "lowerThreshold",
  UPPER_THRESHOLD = "upperThreshold",
  CURRENT_SPEND = "currentSpend",
  MAX_EFFICIENCY = "maxEfficiency",
}

export const markerMap = {
  [MarkerType.LOWER_THRESHOLD]: {
    key: MarkerType.LOWER_THRESHOLD,
    label: "Lower Threshold",
    color: "#4363D8",
    tooltip:
      "The minimum recommended spend for a channel. At this point, the channel is not achieving reach to a critical mass of the target audience or making enough of an impression to affect consumer behavior.",
  },
  [MarkerType.UPPER_THRESHOLD]: {
    key: MarkerType.UPPER_THRESHOLD,
    label: "Upper Threshold",
    color: "#F58231",
    tooltip:
      "The maximum recommended spend for a channel. Beyond this point there are diminishing returns, where every addition dollar spent is returning less revenue than the dollar before it. ",
  },
  [MarkerType.CURRENT_SPEND]: {
    key: MarkerType.CURRENT_SPEND,
    label: "Current Spend",
    color: "#EC0089",
    tooltip:
      "The current mean weekly spend you invested in the channel for this time period, and the mean weekly revenue our model suggests the channel returned.",
  },
  [MarkerType.MAX_EFFICIENCY]: {
    key: MarkerType.MAX_EFFICIENCY,
    label: "Max Efficiency",
    color: "#469990",
    tooltip:
      "This is the point at which the channel has achieved the most efficient return for the marketing spend. Maximum efficiency points are a guide only and may not take into account channel synergies, strategic goals or long-term impacts.",
  },
};
